<script setup>
defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'danger', 'success', 'ghost'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  block: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['click'])
</script>

<template>
  <button
    class="app-button"
    :class="[`variant-${variant}`, `size-${size}`, { block, loading, disabled }]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="button-spinner"></span>
    <span class="button-content">
      <slot />
    </span>
  </button>
</template>

<style scoped>
.app-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-weight: 500;
  border-radius: var(--border-radius);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.size-sm {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-xs);
}

.size-md {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
}

.size-lg {
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
}

.variant-primary {
  background-color: var(--color-primary);
  color: var(--color-white);
}

.variant-primary:hover:not(:disabled) {
  background-color: var(--color-primary-hover);
}

.variant-secondary {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border-color: var(--border-color);
}

.variant-secondary:hover:not(:disabled) {
  background-color: var(--bg-tertiary);
}

.variant-danger {
  background-color: var(--color-error);
  color: var(--color-white);
}

.variant-danger:hover:not(:disabled) {
  background-color: #dc2626;
}

.variant-success {
  background-color: var(--color-success);
  color: var(--color-white);
}

.variant-success:hover:not(:disabled) {
  background-color: #059669;
}

.variant-ghost {
  background-color: transparent;
  color: var(--text-primary);
}

.variant-ghost:hover:not(:disabled) {
  background-color: var(--bg-secondary);
}

.block {
  width: 100%;
}

.disabled,
.loading {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading .button-content {
  opacity: 0.7;
}

.button-content {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.button-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
